import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { SECTION_DEFS } from "@/lib/sections";

const Switch = ({ on, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    role="switch"
    aria-checked={on}
    className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-300 ${
      on ? "bg-gradient-to-r from-[#D4AF37] to-[#AA7C11]" : "bg-[#E5D9CF]"
    }`}
  >
    <span
      className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-[left] duration-300 ease-liquid ${
        on ? "left-6" : "left-1"
      }`}
    />
  </button>
);

export const SectionsTab = () => {
  const { content, refresh } = useContent();
  const [vis, setVis] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setVis(content?.settings?.section_visibility || {});
  }, [content?.settings]);

  const isOn = (key) => vis[key] !== false;
  const toggle = (key) => setVis((v) => ({ ...v, [key]: !isOn(key) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { section_visibility: vis });
      await refresh();
      toast.success("Section visibility saved — live on the website now");
    } catch (e) {
      toast.error("Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-sections-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Section Visibility</h2>
      <p className="mt-1 text-sm font-light text-[#91837C]">
        Switch entire sections of the website on or off — changes go live the moment you save.
      </p>
      {SECTION_DEFS.map((group) => (
        <div key={group.page} className="glass mt-6 rounded-3xl p-6 sm:p-8" data-testid={`sections-group-${group.page.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <h3 className="font-serif text-xl font-semibold text-[#1A1615]">{group.page}</h3>
          <div className="mt-4 space-y-1">
            {group.items.map((item) => (
              <div key={item.key} className="flex items-center justify-between gap-4 rounded-xl px-3 py-3 transition-colors hover:bg-[#F5EBE6]/60">
                <span className="text-sm font-medium text-[#1A1615]">{item.label}</span>
                <Switch on={isOn(item.key)} onClick={() => toggle(item.key)} testid={`section-toggle-${item.key}`} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button onClick={save} disabled={saving} data-testid="sections-save-button" className="btn-gold mt-6 px-8 py-3.5 text-sm disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
        {saving ? "Saving..." : "Save Visibility"}
      </button>
    </div>
  );
};
